import { Spinner } from '@getethos/ethos-design-system-v2';
import {
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { Control, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { MdAdd, MdDelete } from 'react-icons/md';
import styles from '../styles.module.scss';
import { ETIFormData } from '../types';

export interface SectionMetadataFormProps {
  control: Control<Omit<ETIFormData, 'taskId' | 'status'>>;
  className?: string;
  isDisabled: boolean;
  isReadOnly: boolean;
  isLoading: boolean;
}

const SectionMetadataForm = ({
  control,
  className,
  isDisabled,
  isReadOnly,
  isLoading,
}: SectionMetadataFormProps) => {
  const { fields, append, remove } = useFieldArray({
    name: 'sectionMetadata',
    control,
  });

  // Watch the questions to get available sections
  const questions = useWatch({ control, name: 'questions' }) || [];

  // Watch the current sectionMetadata to track selected sections
  const sectionMetadata = useWatch({ control, name: 'sectionMetadata' }) || [];

  // Get unique section names from questions
  const allAvailableSections = useMemo(() => {
    const sections = questions
      .map(question => question.section)
      .filter(Boolean)
      .filter((section, index, array) => array.indexOf(section) === index)
      .sort();
    return sections;
  }, [questions]);

  // Create available sections for a specific row index
  const getAvailableSectionsForRow = useCallback(
    (rowIndex: number) => {
      // Get all sections that are selected in OTHER rows (excluding current row)
      const sectionsSelectedInOtherRows = sectionMetadata
        .map((metadata, idx) =>
          idx !== rowIndex ? metadata?.sectionName : null,
        )
        .filter(Boolean);

      return allAvailableSections.filter(section => {
        // Exclude sections that are already selected in other rows
        return !sectionsSelectedInOtherRows.includes(section);
      });
    },
    [allAvailableSections, sectionMetadata],
  );

  const handleOnAddClick = useCallback(() => {
    append({ sectionName: '', description: '' });
  }, [append]);

  return (
    <div className={className}>
      <TableContainer
        className={styles.TableContainer}
        component={Paper}
        sx={{ overflow: 'visible' }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Section</TableCell>
              <TableCell>Description</TableCell>
              {!isReadOnly && <TableCell align="center">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((fieldItem, index) => (
              <TableRow key={fieldItem.id}>
                <TableCell
                  className={styles.SectionCell}
                  sx={{ verticalAlign: 'top' }}
                >
                  <Controller
                    control={control}
                    name={`sectionMetadata.${index}.sectionName`}
                    render={({ field, fieldState }) => {
                      const availableSectionsForThisRow =
                        getAvailableSectionsForRow(index);

                      return (
                        <FormControl
                          fullWidth
                          variant="standard"
                          error={!!fieldState.error?.message}
                        >
                          <Select
                            disabled={isDisabled || isReadOnly}
                            displayEmpty
                            {...field}
                            value={field.value || ''}
                          >
                            <MenuItem value="" disabled>
                              <em>Select Section</em>
                            </MenuItem>
                            {availableSectionsForThisRow.map(section => (
                              <MenuItem key={section} value={section}>
                                {section}
                              </MenuItem>
                            ))}
                          </Select>
                          {fieldState.error?.message && (
                            <FormHelperText>
                              {fieldState.error.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      );
                    }}
                  />
                </TableCell>
                <TableCell
                  className={styles.Cell}
                  sx={{ verticalAlign: 'top' }}
                >
                  <Controller
                    control={control}
                    name={`sectionMetadata.${index}.description`}
                    render={({ field, fieldState }) => (
                      <TextField
                        disabled={isDisabled}
                        fullWidth
                        multiline
                        minRows={1}
                        placeholder="Description"
                        error={!!fieldState.error?.message}
                        helperText={fieldState.error?.message}
                        variant="standard"
                        slotProps={{ input: { readOnly: isReadOnly } }}
                        {...field}
                        onBlur={e => {
                          field.onBlur();
                          // Trim the value when user loses focus
                          if (e.target.value !== e.target.value.trim()) {
                            field.onChange(e.target.value.trim());
                          }
                        }}
                      />
                    )}
                  />
                </TableCell>
                {!isReadOnly && (
                  <TableCell className={styles.ActionsCell} align="center">
                    <IconButton
                      disabled={isDisabled}
                      onClick={() => remove(index)}
                    >
                      <MdDelete />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {isLoading && (
              <TableRow>
                <TableCell align="center" colSpan={isReadOnly ? 2 : 3}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && fields.length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={isReadOnly ? 2 : 3}>
                  No section descriptions
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!isReadOnly && (
        <Button
          size="small"
          variant="contained"
          className={styles.AddQuestionButton}
          disabled={isDisabled}
          onClick={handleOnAddClick}
          sx={{ marginBlock: '1.5rem', marginRight: '0.5rem' }}
        >
          <MdAdd />
          Add Section Description
        </Button>
      )}
    </div>
  );
};

export default SectionMetadataForm;
